import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/** -----------------------------
 * Generic helper to follow Learn REST paging (results + paging.nextPage)
 * ----------------------------*/
const fetchPagedResults = async (platformUrl, initialPath, headers) => {
    let results = [];
    let nextPath = initialPath;

    while (nextPath) {
        const resp = await axios.get(`${platformUrl}${nextPath}`, { headers });
        const data = resp.data || {};

        if (Array.isArray(data.results)) {
            results = results.concat(data.results);
        }

        const paging = data.paging || {};
        nextPath = paging.nextPage ? paging.nextPage : null;
    }

    return results;
};

const normalizeBase = (u) => String(u || "").trim().replace(/\/+$/, "");
const safeStr = (v) => (v === null || v === undefined ? "" : String(v));
const asInt = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : fallback;
};

const isAvailableYes = (x) => safeStr(x?.availability?.available || "Yes").toLowerCase() === "yes";
const isStudentMembership = (m) => safeStr(m?.courseRoleId).toLowerCase() === "student";

/** -----------------------------
 * Learn API wrappers (read-only discovery)
 * ----------------------------*/
const listCoursesByDataSource = async (platformUrl, dataSourceIdQuery, headers) => {
    const base = normalizeBase(platformUrl);

    const url = `${base}/learn/api/public/v3/courses`;
    const resp = await axios.get(url, {
        headers,
        params: {
            dataSourceId: dataSourceIdQuery,
            limit: 100,
        },
    });

    const data = resp.data || {};
    return Array.isArray(data.results) ? data.results : [];
};

const getCourseMemberships = async (platformUrl, courseRef, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v1/courses/${encodeURIComponent(courseRef)}/users?limit=200&expand=user`,
        headers
    );
};

const getGradebookColumns = async (platformUrl, courseRef, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v2/courses/${encodeURIComponent(courseRef)}/gradebook/columns?limit=200`,
        headers
    );
};

const getCourseContents = async (platformUrl, courseRef, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v1/courses/${encodeURIComponent(courseRef)}/contents?limit=200`,
        headers
    );
};

const getDiscussions = async (platformUrl, courseRef, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v1/courses/${encodeURIComponent(courseRef)}/discussions?limit=200`,
        headers
    );
};

/** -----------------------------
 * Backend API wrappers
 * ----------------------------*/
const buildBackendHeaders = ({ token, tenantId, oneTimeKey }) => {
    const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    };

    if (tenantId) headers["X-Tenant-Id"] = tenantId;
    if (oneTimeKey) headers["X-One-Time-Key"] = oneTimeKey;

    return headers;
};

const previewSimulation = async ({
                                     backEndUrl,
                                     platformUrl,
                                     token,
                                     tenantId,
                                     userUuid,
                                     oneTimeKey,
                                     payload,
                                 }) => {
    const url = `${normalizeBase(backEndUrl)}/faculty-simulator/preview`;

    const resp = await axios.post(url, payload, {
        params: {
            platformUrl: normalizeBase(platformUrl),
            userUuid,
        },
        headers: buildBackendHeaders({ token, tenantId, oneTimeKey }),
    });

    return resp.data || {};
};

const startSimulation = async ({
                                   backEndUrl,
                                   platformUrl,
                                   token,
                                   tenantId,
                                   userUuid,
                                   oneTimeKey,
                                   payload,
                               }) => {
    const url = `${normalizeBase(backEndUrl)}/faculty-simulator/start`;

    const resp = await axios.post(url, payload, {
        params: {
            platformUrl: normalizeBase(platformUrl),
            userUuid,
        },
        headers: buildBackendHeaders({ token, tenantId, oneTimeKey }),
    });

    return resp.data || {};
};

const postRunAction = async ({
                                 backEndUrl,
                                 platformUrl,
                                 token,
                                 tenantId,
                                 userUuid,
                                 oneTimeKey,
                                 action,
                                 runId,
                             }) => {
    const url = `${normalizeBase(backEndUrl)}/faculty-simulator/${action}`;

    const resp = await axios.post(
        url,
        { runId },
        {
            params: {
                platformUrl: normalizeBase(platformUrl),
                userUuid,
            },
            headers: buildBackendHeaders({ token, tenantId, oneTimeKey }),
        }
    );

    return resp.data || {};
};

const getRunStatus = async ({
                                backEndUrl,
                                platformUrl,
                                token,
                                tenantId,
                                userUuid,
                                runId,
                            }) => {
    const url = `${normalizeBase(backEndUrl)}/faculty-simulator/run-status`;

    const resp = await axios.get(url, {
        params: {
            runId,
            platformUrl: normalizeBase(platformUrl),
            userUuid,
        },
        headers: buildBackendHeaders({ token, tenantId }),
    });

    return resp.data || {};
};

/** -----------------------------
 * Helpers for display
 * ----------------------------*/
const getContentTitle = (c) => c?.title || c?.name || c?.id || "(untitled)";
const getColumnName = (c) => c?.name || c?.externalId || c?.id || "(unnamed)";
const getDiscussionName = (d) => d?.title || d?.name || d?.id || "(untitled)";

const summarizeCourseContents = (contents) => {
    const out = {
        assignments: [],
        tests: [],
        discussions: [],
        other: [],
    };

    for (const c of contents) {
        const t = safeStr(c?.contentHandler?.id || c?.contentHandler?.url || c?.contentHandler?.type).toLowerCase();
        const title = getContentTitle(c);

        if (t.includes("assignment")) {
            out.assignments.push({ id: c.id, title });
        } else if (t.includes("test") || t.includes("assessment")) {
            out.tests.push({ id: c.id, title });
        } else if (t.includes("discussion")) {
            out.discussions.push({ id: c.id, title });
        } else {
            out.other.push({ id: c.id, title });
        }
    }

    return out;
};

const buildScenarioPayload = ({
                                  selectedCourse,
                                  students,
                                  attemptColumns,
                                  discussions,
                                  config,
                              }) => {
    return {
        courseId: selectedCourse?.id || "",
        compressionRatioMinutesPerSimDay: config.compressionRatioMinutesPerSimDay,
        durationSimDays: config.durationSimDays,
        maxEvents: config.maxEvents,
        includeEvents: {
            newStudents: config.includeNewStudents,
            studentDrops: config.includeStudentDrops,
            assignmentSubmissions: config.includeAssignmentSubmissions,
            assignmentResubmissions: config.includeAssignmentResubmissions,
            discussionPosts: config.includeDiscussionPosts,
            discussionReplies: config.includeDiscussionReplies,
            testSubmissions: config.includeTestSubmissions,
            courseMessages: config.includeCourseMessages,
            ltiGradeUpdates: config.includeLtiGradeUpdates,
        },
        pacing: {
            earlySubmissionRate: config.earlySubmissionRate,
            onTimeSubmissionRate: config.onTimeSubmissionRate,
            lateSubmissionRate: config.lateSubmissionRate,
            resubmissionRate: config.resubmissionRate,
            messageRate: config.messageRate,
            dropRate: config.dropRate,
        },
        limits: {
            maxNewStudents: config.maxNewStudents,
            maxDrops: config.maxDrops,
            maxAssignments: config.maxAssignments,
            maxTests: config.maxTests,
            maxDiscussionThreads: config.maxDiscussionThreads,
            maxMessages: config.maxMessages,
            maxGradeSyncs: config.maxGradeSyncs,
        },
        discoveredContext: {
            currentStudentCount: students.length,
            assignmentColumns: attemptColumns.slice(0, config.maxAssignments).map((c) => ({
                id: c.id,
                name: c.name,
                due: c.due || null,
                possible: c.possible ?? null,
            })),
            discussions: discussions.slice(0, config.maxDiscussionThreads).map((d) => ({
                id: d.id,
                title: getDiscussionName(d),
            })),
        },
    };
};

/** -----------------------------
 * Component
 * ----------------------------*/
const FacultyInteractionAgent = ({ platformUrl, backEndUrl, tenantId }) => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const userUuid = params.get("uuid");

    const [dataSourceIdQuery, setDataSourceIdQuery] = useState("externalId:TRAINING");

    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState("");

    const [memberships, setMemberships] = useState([]);
    const [columns, setColumns] = useState([]);
    const [contents, setContents] = useState([]);
    const [discussions, setDiscussions] = useState([]);

    const [loadingCourses, setLoadingCourses] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [error, setError] = useState(null);

    const [oneTimeKey, setOneTimeKey] = useState("");

    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [previewResult, setPreviewResult] = useState(null);

    const [runActionLoading, setRunActionLoading] = useState(false);
    const [runActionError, setRunActionError] = useState(null);
    const [runActionResult, setRunActionResult] = useState(null);

    const [runId, setRunId] = useState("");
    const [runStatusLoading, setRunStatusLoading] = useState(false);
    const [runStatus, setRunStatus] = useState(null);

    const courseLoadNonceRef = useRef(0);
    const runPollTimerRef = useRef(null);

    const selectedCourse = useMemo(
        () => courses.find((c) => c.id === selectedCourseId) || null,
        [courses, selectedCourseId]
    );

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [config, setConfig] = useState({
        compressionRatioMinutesPerSimDay: 3,
        durationSimDays: 7,
        maxEvents: 50,

        includeNewStudents: true,
        includeStudentDrops: true,
        includeAssignmentSubmissions: true,
        includeAssignmentResubmissions: true,
        includeDiscussionPosts: true,
        includeDiscussionReplies: true,
        includeTestSubmissions: true,
        includeCourseMessages: true,
        includeLtiGradeUpdates: true,

        earlySubmissionRate: 0.2,
        onTimeSubmissionRate: 0.6,
        lateSubmissionRate: 0.2,
        resubmissionRate: 0.25,
        messageRate: 0.3,
        dropRate: 0.1,

        maxNewStudents: 3,
        maxDrops: 2,
        maxAssignments: 10,
        maxTests: 10,
        maxDiscussionThreads: 10,
        maxMessages: 10,
        maxGradeSyncs: 10,
    });

    const setNumberConfig = (key, fallback = 0) => (e) => {
        setConfig((prev) => ({
            ...prev,
            [key]: asInt(e.target.value, fallback),
        }));
    };

    const setFloatConfig = (key, fallback = 0) => (e) => {
        const n = Number(e.target.value);
        setConfig((prev) => ({
            ...prev,
            [key]: Number.isFinite(n) ? n : fallback,
        }));
    };

    const setBoolConfig = (key) => (e) => {
        setConfig((prev) => ({
            ...prev,
            [key]: !!e.target.checked,
        }));
    };

    const loadCourses = async () => {
        setError(null);
        setPreviewError(null);
        setRunActionError(null);

        try {
            if (!token) throw new Error("Missing token in URL.");
            setLoadingCourses(true);

            const list = await listCoursesByDataSource(platformUrl, dataSourceIdQuery, headers);
            setCourses(list);
            setSelectedCourseId(list[0]?.id || "");
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Failed to load courses.");
        } finally {
            setLoadingCourses(false);
        }
    };

    const loadCourseDetail = async (course) => {
        if (!course) return;

        const myNonce = ++courseLoadNonceRef.current;

        setError(null);
        setPreviewError(null);
        setRunActionError(null);

        setLoadingDetail(true);
        setMemberships([]);
        setColumns([]);
        setContents([]);
        setDiscussions([]);
        setPreviewResult(null);

        try {
            const courseRef = course.id;

            const [mem, cols, cnt, disc] = await Promise.all([
                getCourseMemberships(platformUrl, courseRef, headers),
                getGradebookColumns(platformUrl, courseRef, headers),
                getCourseContents(platformUrl, courseRef, headers),
                getDiscussions(platformUrl, courseRef, headers),
            ]);

            if (courseLoadNonceRef.current !== myNonce) return;

            setMemberships(mem);
            setColumns(cols);
            setContents(cnt);
            setDiscussions(disc);
        } catch (e) {
            if (courseLoadNonceRef.current !== myNonce) return;
            setError(e?.response?.data?.message || e?.message || "Failed to load course details.");
        } finally {
            if (courseLoadNonceRef.current === myNonce) {
                setLoadingDetail(false);
            }
        }
    };

    const stopPollingRun = () => {
        if (runPollTimerRef.current) {
            window.clearInterval(runPollTimerRef.current);
            runPollTimerRef.current = null;
        }
    };

    const refreshRunStatus = async (runIdValue) => {
        try {
            if (!runIdValue) return;
            if (!backEndUrl) return;
            if (!token) return;
            if (!userUuid) return;

            setRunStatusLoading(true);

            const data = await getRunStatus({
                backEndUrl,
                platformUrl,
                token,
                tenantId,
                userUuid,
                runId: runIdValue,
            });

            setRunStatus(data || null);
        } catch (e) {
            setRunActionError(
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                "Failed to load run status."
            );
        } finally {
            setRunStatusLoading(false);
        }
    };

    const startPollingRun = (runIdValue) => {
        stopPollingRun();

        if (!runIdValue) return;

        refreshRunStatus(runIdValue);

        runPollTimerRef.current = window.setInterval(() => {
            refreshRunStatus(runIdValue);
        }, 8000);
    };

    useEffect(() => {
        loadCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedCourse) return;
        loadCourseDetail(selectedCourse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourseId]);

    useEffect(() => {
        return () => {
            stopPollingRun();
        };
    }, []);

    useEffect(() => {
        const status = safeStr(runStatus?.status).toLowerCase();
        if (status === "stopped" || status === "completed" || status === "failed") {
            stopPollingRun();
        }
    }, [runStatus]);

    const students = useMemo(() => {
        return memberships
            .filter(isAvailableYes)
            .filter(isStudentMembership)
            .map((m) => {
                const u = m.user || {};
                const displayName = `${safeStr(u?.name?.given)} ${safeStr(u?.name?.family)}`.trim();

                return {
                    membershipId: m.id,
                    userId: m.userId,
                    userUuid: u.uuid,
                    userName: u.userName,
                    displayName: displayName || u.userName || m.userId,
                };
            });
    }, [memberships]);

    const attemptColumns = useMemo(() => {
        return columns
            .filter(isAvailableYes)
            .filter((c) => safeStr(c?.grading?.type).toLowerCase() === "attempts")
            .map((c) => ({
                id: c.id,
                name: getColumnName(c),
                due: c?.grading?.due || null,
                possible: c?.score?.possible ?? null,
            }));
    }, [columns]);

    const contentSummary = useMemo(() => summarizeCourseContents(contents), [contents]);

    const scenarioPayload = useMemo(() => {
        if (!selectedCourse) return null;

        return buildScenarioPayload({
            selectedCourse,
            students,
            attemptColumns,
            discussions,
            config,
        });
    }, [selectedCourse, students, attemptColumns, discussions, config]);

    const handlePreviewSimulation = async () => {
        setPreviewError(null);
        setRunActionError(null);
        setPreviewResult(null);

        try {
            if (!tenantId) throw new Error("Missing tenantId prop.");
            if (!backEndUrl) throw new Error("Missing backEndUrl prop.");
            if (!token) throw new Error("Missing token in URL.");
            if (!userUuid) throw new Error("Missing userUuid in URL.");
            if (!selectedCourse) throw new Error("No course selected.");
            if (!scenarioPayload) throw new Error("Unable to build scenario payload.");

            setPreviewLoading(true);

            const data = await previewSimulation({
                backEndUrl,
                platformUrl,
                token,
                tenantId,
                userUuid,
                oneTimeKey: oneTimeKey.trim() || undefined,
                payload: scenarioPayload,
            });

            setPreviewResult(data || {});
        } catch (e) {
            setPreviewError(
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                "Failed to preview simulation."
            );
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleStartSimulation = async () => {
        setRunActionError(null);
        setRunActionResult(null);

        try {
            if (!tenantId) throw new Error("Missing tenantId prop.");
            if (!backEndUrl) throw new Error("Missing backEndUrl prop.");
            if (!token) throw new Error("Missing token in URL.");
            if (!userUuid) throw new Error("Missing userUuid in URL.");
            if (!selectedCourse) throw new Error("No course selected.");
            if (!oneTimeKey.trim()) throw new Error("Enter the one-time key.");
            if (!scenarioPayload) throw new Error("Unable to build scenario payload.");

            setRunActionLoading(true);

            const data = await startSimulation({
                backEndUrl,
                platformUrl,
                token,
                tenantId,
                userUuid,
                oneTimeKey: oneTimeKey.trim(),
                payload: scenarioPayload,
            });

            setRunActionResult(data || { ok: true });

            const newRunId = data?.runId || "";
            setRunId(newRunId);

            if (newRunId) {
                startPollingRun(newRunId);
            }
        } catch (e) {
            setRunActionError(
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                "Failed to start simulation."
            );
        } finally {
            setRunActionLoading(false);
        }
    };

    const handleRunAction = async (action) => {
        setRunActionError(null);
        setRunActionResult(null);

        try {
            if (!tenantId) throw new Error("Missing tenantId prop.");
            if (!backEndUrl) throw new Error("Missing backEndUrl prop.");
            if (!token) throw new Error("Missing token in URL.");
            if (!userUuid) throw new Error("Missing userUuid in URL.");
            if (!runId.trim()) throw new Error("No runId available.");

            setRunActionLoading(true);

            const data = await postRunAction({
                backEndUrl,
                platformUrl,
                token,
                tenantId,
                userUuid,
                oneTimeKey: oneTimeKey.trim() || undefined,
                action,
                runId: runId.trim(),
            });

            setRunActionResult(data || { ok: true });

            if (action === "stop") {
                stopPollingRun();
            } else {
                startPollingRun(runId.trim());
            }

            await refreshRunStatus(runId.trim());
        } catch (e) {
            setRunActionError(
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                `Failed to ${action} simulation.`
            );
        } finally {
            setRunActionLoading(false);
        }
    };

    const eventPreview = Array.isArray(previewResult?.events) ? previewResult.events : [];
    const recentEvents = Array.isArray(runStatus?.recentEvents) ? runStatus.recentEvents : [];
    const backendRunState = safeStr(runStatus?.status || runActionResult?.status || "");

    return (
        <div className="bb-container">
            <h2>Faculty Interaction Agent</h2>

            {!token && (
                <div className="bb-error" style={{ color: "red", marginBottom: "0.75rem" }}>
                    Missing token in URL. This tool must be launched via LTI with a token parameter.
                </div>
            )}

            {!userUuid && (
                <div className="bb-error" style={{ color: "red", marginBottom: "0.75rem" }}>
                    Missing userUuid in URL. This tool must be launched via LTI with a userUuid parameter.
                </div>
            )}

            {error && (
                <div className="bb-error" style={{ color: "red", marginBottom: "0.75rem" }}>
                    {error}
                </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", marginBottom: "1rem" }}>
                <div style={{ minWidth: 380 }}>
                    <label>
                        <strong>Data Source Selector (course query)</strong>
                    </label>
                    <input
                        style={{ width: "100%" }}
                        value={dataSourceIdQuery}
                        onChange={(e) => setDataSourceIdQuery(e.target.value)}
                        placeholder="e.g., externalId:TRAINING"
                    />
                    <div style={{ fontSize: "0.85rem", opacity: 0.8, marginTop: "0.25rem" }}>
                        Used for <code>/learn/api/public/v3/courses?dataSourceId=...</code>
                    </div>
                </div>

                <button className="bb-button" onClick={loadCourses} disabled={loadingCourses || !token}>
                    {loadingCourses ? "Loading courses..." : "Load courses"}
                </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "1rem" }}>
                <div>
                    <h3>Courses ({courses.length})</h3>

                    <select
                        style={{ width: "100%" }}
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value)}
                        disabled={courses.length === 0}
                    >
                        {courses.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name} ({c.courseId})
                            </option>
                        ))}
                    </select>

                    {selectedCourse && (
                        <div style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
                            <div>
                                <strong>Selected:</strong> {selectedCourse.name}
                            </div>
                            <div>
                                <strong>CourseId:</strong> {selectedCourse.courseId}
                            </div>
                            <div>
                                <strong>Internal Id:</strong> {selectedCourse.id}
                            </div>
                            <div>
                                <strong>Course dataSourceId:</strong> {selectedCourse.dataSourceId}
                            </div>
                            {loadingDetail && <div style={{ marginTop: "0.5rem" }}>Loading course detail…</div>}
                        </div>
                    )}

                    <hr style={{ margin: "1rem 0" }} />

                    <h3>Simulation Controls</h3>

                    <div style={{ fontSize: "0.85rem", opacity: 0.85, marginBottom: "0.5rem" }}>
                        Enter a one-time key issued by your backend. The backend should validate the key and enforce
                        simulation safeguards.
                    </div>

                    <input
                        style={{ width: "100%", marginBottom: "0.75rem" }}
                        value={oneTimeKey}
                        onChange={(e) => setOneTimeKey(e.target.value)}
                        placeholder="One-time key"
                    />

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "0.75rem" }}>
                        <div>
                            <label>Minutes per simulated day</label>
                            <input
                                type="number"
                                min="1"
                                style={{ width: "100%" }}
                                value={config.compressionRatioMinutesPerSimDay}
                                onChange={setNumberConfig("compressionRatioMinutesPerSimDay", 3)}
                            />
                        </div>

                        <div>
                            <label>Simulated days</label>
                            <input
                                type="number"
                                min="1"
                                style={{ width: "100%" }}
                                value={config.durationSimDays}
                                onChange={setNumberConfig("durationSimDays", 7)}
                            />
                        </div>

                        <div>
                            <label>Max events</label>
                            <input
                                type="number"
                                min="1"
                                style={{ width: "100%" }}
                                value={config.maxEvents}
                                onChange={setNumberConfig("maxEvents", 50)}
                            />
                        </div>

                        <div>
                            <label>Max new students</label>
                            <input
                                type="number"
                                min="0"
                                style={{ width: "100%" }}
                                value={config.maxNewStudents}
                                onChange={setNumberConfig("maxNewStudents", 3)}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: "0.75rem" }}>
                        <strong>Event Types</strong>
                        <div><label><input type="checkbox" checked={config.includeNewStudents} onChange={setBoolConfig("includeNewStudents")} /> New students arrive</label></div>
                        <div><label><input type="checkbox" checked={config.includeStudentDrops} onChange={setBoolConfig("includeStudentDrops")} /> Students drop</label></div>
                        <div><label><input type="checkbox" checked={config.includeAssignmentSubmissions} onChange={setBoolConfig("includeAssignmentSubmissions")} /> Assignment submissions</label></div>
                        <div><label><input type="checkbox" checked={config.includeAssignmentResubmissions} onChange={setBoolConfig("includeAssignmentResubmissions")} /> Assignment resubmissions</label></div>
                        <div><label><input type="checkbox" checked={config.includeDiscussionPosts} onChange={setBoolConfig("includeDiscussionPosts")} /> Discussion top-level posts</label></div>
                        <div><label><input type="checkbox" checked={config.includeDiscussionReplies} onChange={setBoolConfig("includeDiscussionReplies")} /> Discussion replies</label></div>
                        <div><label><input type="checkbox" checked={config.includeTestSubmissions} onChange={setBoolConfig("includeTestSubmissions")} /> Test submissions</label></div>
                        <div><label><input type="checkbox" checked={config.includeCourseMessages} onChange={setBoolConfig("includeCourseMessages")} /> Course messages</label></div>
                        <div><label><input type="checkbox" checked={config.includeLtiGradeUpdates} onChange={setBoolConfig("includeLtiGradeUpdates")} /> LTI grade updates</label></div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "0.75rem" }}>
                        <div>
                            <label>Early rate</label>
                            <input
                                type="number"
                                step="0.05"
                                min="0"
                                max="1"
                                style={{ width: "100%" }}
                                value={config.earlySubmissionRate}
                                onChange={setFloatConfig("earlySubmissionRate", 0.2)}
                            />
                        </div>
                        <div>
                            <label>On-time rate</label>
                            <input
                                type="number"
                                step="0.05"
                                min="0"
                                max="1"
                                style={{ width: "100%" }}
                                value={config.onTimeSubmissionRate}
                                onChange={setFloatConfig("onTimeSubmissionRate", 0.6)}
                            />
                        </div>
                        <div>
                            <label>Late rate</label>
                            <input
                                type="number"
                                step="0.05"
                                min="0"
                                max="1"
                                style={{ width: "100%" }}
                                value={config.lateSubmissionRate}
                                onChange={setFloatConfig("lateSubmissionRate", 0.2)}
                            />
                        </div>
                        <div>
                            <label>Resubmission rate</label>
                            <input
                                type="number"
                                step="0.05"
                                min="0"
                                max="1"
                                style={{ width: "100%" }}
                                value={config.resubmissionRate}
                                onChange={setFloatConfig("resubmissionRate", 0.25)}
                            />
                        </div>
                        <div>
                            <label>Message rate</label>
                            <input
                                type="number"
                                step="0.05"
                                min="0"
                                max="1"
                                style={{ width: "100%" }}
                                value={config.messageRate}
                                onChange={setFloatConfig("messageRate", 0.3)}
                            />
                        </div>
                        <div>
                            <label>Drop rate</label>
                            <input
                                type="number"
                                step="0.05"
                                min="0"
                                max="1"
                                style={{ width: "100%" }}
                                value={config.dropRate}
                                onChange={setFloatConfig("dropRate", 0.1)}
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                        <button
                            className="bb-button"
                            onClick={handlePreviewSimulation}
                            disabled={previewLoading || !selectedCourse || !token || !userUuid}
                        >
                            {previewLoading ? "Previewing..." : "Preview timeline"}
                        </button>

                        <button
                            className="bb-button"
                            onClick={handleStartSimulation}
                            disabled={runActionLoading || !selectedCourse || !token || !userUuid}
                        >
                            {runActionLoading ? "Starting..." : "Start simulation"}
                        </button>

                        <button
                            className="bb-button"
                            onClick={() => handleRunAction("pause")}
                            disabled={runActionLoading || !runId}
                        >
                            Pause
                        </button>

                        <button
                            className="bb-button"
                            onClick={() => handleRunAction("resume")}
                            disabled={runActionLoading || !runId}
                        >
                            Resume
                        </button>

                        <button
                            className="bb-button"
                            onClick={() => handleRunAction("stop")}
                            disabled={runActionLoading || !runId}
                        >
                            Stop
                        </button>
                    </div>

                    {previewError && <div style={{ color: "red", marginBottom: "0.5rem" }}>{previewError}</div>}
                    {runActionError && <div style={{ color: "red", marginBottom: "0.5rem" }}>{runActionError}</div>}

                    {runId && (
                        <div style={{ fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                            <strong>RunId:</strong> {runId}
                        </div>
                    )}

                    {backendRunState && (
                        <div style={{ fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                            <strong>Run status:</strong> {backendRunState}
                            {runStatusLoading ? " (refreshing...)" : ""}
                        </div>
                    )}

                    {runActionResult && (
                        <pre
                            style={{
                                marginTop: "0.75rem",
                                background: "#f6f6f6",
                                padding: "0.5rem",
                                overflowX: "auto",
                            }}
                        >
                            {JSON.stringify(runActionResult, null, 2)}
                        </pre>
                    )}
                </div>

                <div>
                    <h3>
                        Course Discovery {loadingDetail && <span style={{ fontSize: "0.9rem" }}>(loading...)</span>}
                    </h3>

                    <div style={{ marginBottom: "1rem" }}>
                        <strong>Enrollments:</strong> {memberships.length} (students: {students.length})
                    </div>

                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>Metric</th>
                            <th>Count</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>Students</td>
                            <td>{students.length}</td>
                        </tr>
                        <tr>
                            <td>Attempt-based grade columns</td>
                            <td>{attemptColumns.length}</td>
                        </tr>
                        <tr>
                            <td>Assignments (content scan)</td>
                            <td>{contentSummary.assignments.length}</td>
                        </tr>
                        <tr>
                            <td>Tests (content scan)</td>
                            <td>{contentSummary.tests.length}</td>
                        </tr>
                        <tr>
                            <td>Discussions (content scan)</td>
                            <td>{contentSummary.discussions.length}</td>
                        </tr>
                        <tr>
                            <td>Discussions (API)</td>
                            <td>{discussions.length}</td>
                        </tr>
                        </tbody>
                    </table>

                    <h4>Students</h4>
                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Username</th>
                            <th>UserId</th>
                        </tr>
                        </thead>
                        <tbody>
                        {students.map((s) => (
                            <tr key={s.userId}>
                                <td>{s.displayName}</td>
                                <td>{s.userName || "-"}</td>
                                <td>{s.userId}</td>
                            </tr>
                        ))}
                        {students.length === 0 && (
                            <tr>
                                <td colSpan={3}>No student enrollments found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    <h4>Attempt-based Gradebook Columns</h4>
                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Due</th>
                            <th>Points</th>
                            <th>ColumnId</th>
                        </tr>
                        </thead>
                        <tbody>
                        {attemptColumns.slice(0, 15).map((a) => (
                            <tr key={a.id}>
                                <td>{a.name}</td>
                                <td>{a.due || "-"}</td>
                                <td>{a.possible ?? "-"}</td>
                                <td>{a.id}</td>
                            </tr>
                        ))}
                        {attemptColumns.length === 0 && (
                            <tr>
                                <td colSpan={4}>No attempt-based columns found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    <h4>Discussions</h4>
                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>Title</th>
                            <th>Id</th>
                        </tr>
                        </thead>
                        <tbody>
                        {discussions.slice(0, 15).map((d) => (
                            <tr key={d.id}>
                                <td>{getDiscussionName(d)}</td>
                                <td>{d.id}</td>
                            </tr>
                        ))}
                        {discussions.length === 0 && (
                            <tr>
                                <td colSpan={2}>No discussions found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    <h4>Preview Timeline</h4>
                    <div style={{ fontSize: "0.85rem", opacity: 0.8, marginBottom: "0.5rem" }}>
                        This is the simulated event plan returned by your backend before the run starts.
                    </div>

                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>#</th>
                            <th>When</th>
                            <th>Type</th>
                            <th>Target</th>
                        </tr>
                        </thead>
                        <tbody>
                        {eventPreview.slice(0, 25).map((evt, idx) => (
                            <tr key={`${safeStr(evt?.eventId || idx)}-${idx}`}>
                                <td>{idx + 1}</td>
                                <td>{evt?.scheduledAt || evt?.offset || "-"}</td>
                                <td>{evt?.type || "-"}</td>
                                <td>{evt?.targetName || evt?.targetId || "-"}</td>
                            </tr>
                        ))}
                        {eventPreview.length === 0 && (
                            <tr>
                                <td colSpan={4}>No preview loaded.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    <h4>Recent Run Events</h4>
                    <div style={{ fontSize: "0.85rem", opacity: 0.8, marginBottom: "0.5rem" }}>
                        These are the most recent run events returned by your backend status endpoint.
                    </div>

                    <table className="bb-table" style={{ width: "100%" }}>
                        <thead>
                        <tr>
                            <th>When</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Detail</th>
                        </tr>
                        </thead>
                        <tbody>
                        {recentEvents.slice(0, 25).map((evt, idx) => (
                            <tr key={`${safeStr(evt?.eventId || idx)}-${idx}`}>
                                <td>{evt?.executedAt || evt?.scheduledAt || "-"}</td>
                                <td>{evt?.type || "-"}</td>
                                <td>{evt?.state || evt?.status || "-"}</td>
                                <td>{evt?.lastDetail || evt?.detail || evt?.targetName || "-"}</td>
                            </tr>
                        ))}
                        {recentEvents.length === 0 && (
                            <tr>
                                <td colSpan={4}>No run events yet.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FacultyInteractionAgent;